import React, { useState, useCallback, useEffect } from 'react';
import { Button } from 'components/_new/Button';
import { useTranslation } from 'react-i18next';
import { InputTextArea } from '../../../_new/InputTextArea';
import { Icon } from '../../../_new/Icon';

interface RatingProps {
	value?: number;
	comment?: string;
	onChange: (value: number) => void;
	onCommentChange?: (comment: string) => void;
	unselectable?: boolean;
	loading?: boolean;
}

export const Rating = ({
	value,
	comment = '',
	onChange,
	onCommentChange,
	unselectable = false,
	loading = false,
}: RatingProps) => {
	const { t } = useTranslation();
	const [hover, setHover] = useState<number>(0);
	const [showCommentForm, setShowCommentForm] = useState<boolean>(false);
	const [commentValue, setCommentValue] = useState<string>(comment);
	const [commentSubmitted, setCommentSubmitted] = useState<boolean>(!!comment);

	useEffect(() => {
		if (value === 0) {
			setCommentSubmitted(false);
			setShowCommentForm(false);
		}
	}, [value]);

	useEffect(() => {
		setCommentValue(comment);
		setCommentSubmitted(!!comment);
	}, [comment]);

	const faces = ['tired', 'frown', 'meh', 'smile', 'grin-stars'];

	const handleRatingClick = useCallback(
		(index: number) => {
			const currentValue = typeof value === 'number' ? value : 0;
			const newRating = unselectable && index === currentValue - 1 ? 0 : index + 1;

			const isClickingSameRating = newRating === currentValue;

			if (onChange) {
				if (!isClickingSameRating) {
					onChange(newRating);
				}

				if (newRating > 0) {
					if (isClickingSameRating && commentSubmitted) {
						setCommentSubmitted(false);
						setShowCommentForm(true);
					} else {
						setShowCommentForm(true);
					}
				} else {
					setShowCommentForm(false);
				}
			}

			setHover(0);
		},
		[onChange, unselectable, value, commentSubmitted]
	);

	const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setCommentValue(e.target.value);
	}, []);

	const handleSubmitComment = useCallback(() => {
		onCommentChange?.(commentValue);
		setCommentSubmitted(true);
		setShowCommentForm(false);
	}, [commentValue, onCommentChange]);

	const handleCloseComment = useCallback(() => {
		if (comment) {
			setCommentValue(comment);
			setCommentSubmitted(true);
		}
		setShowCommentForm(false);
	}, [comment]);

	return (
		<div className="flex flex-column gap-3 w-full">
			<div className="flex flex-row gap-4 align-items-center flex-wrap">
				<div className="flex flex-row gap-3 justify-content-between">
					{faces.map((face, index) => {
						const actualIndex = index + 1;
						// Handle both value and hover safely with fallbacks
						const activeIndex = hover || (typeof value === 'number' ? value : 0);
						const variant = actualIndex === activeIndex ? 'primary-text' : 'grey-text';

						return (
							<Button
								key={face}
								onClick={() => {
									handleRatingClick(index);
								}}
								variant={variant}
								unactive
								label={face}
								noTitle
								icon={`face-${face} fa-2x`}
								iconStyle="regular"
								iconOnly
								variantSize="sm"
								onMouseEnter={() => {
									setHover(actualIndex);
								}}
								onMouseLeave={() => {
									setHover(0);
								}}
								rounded
								disabled={loading}
							/>
						);
					})}
				</div>

				{commentSubmitted && (
					<div className="flex flex-row gap-2 align-items-center text-success mt-2 md:mt-0">
						<Icon name="check" />
						<span className="hidden md:inline">{t('userPanel.sessionsNew.rating.thankYouMessage')}</span>
					</div>
				)}
			</div>

			{showCommentForm && !commentSubmitted && (
				<div className="flex flex-column gap-2 mt-2 w-full">
					<InputTextArea
						value={commentValue}
						onChange={handleCommentChange}
						rows={3}
						placeholder={t('userPanel.sessionsNew.rating.commentPlaceholder')}
						className="w-full"
						maxLength={200}
					/>
					<div className="flex justify-content-between">
						<Button
							variant="primary-outlined"
							label={t('actions.close')}
							variantSize="sm"
							onClick={handleCloseComment}
						/>
						<Button
							label={t('userPanel.sessionsNew.rating.saveComment')}
							variant="primary"
							variantSize="sm"
							onClick={handleSubmitComment}
							loading={loading}
						/>
					</div>
				</div>
			)}
		</div>
	);
};
